// app/frontend/pages/Admin/Changelog/Index.tsx
import { useState } from "react";
import {
  Box,
  Stack,
  Group,
  Table,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  Select,
  Pagination,
} from "@mantine/core";
import {
  IconSearch,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import { Link, router } from "@inertiajs/react";
import { L2KH2, L2KText, L2KCaption } from "../../../components/ui/Typography";
import { L2KButton } from "../../../components/ui/Forms";
import { l2kNotify } from "../../../components/ui/Feedback";
import AdminLayout from "../../../components/layouts/AdminLayout";
import { SEO } from "../../../components/common/SEO";

interface ChangelogEntry {
  id: number;
  slug: string;
  title: string;
  description: string;
  category: string;
  category_label: string;
  category_color: string;
  status: string;
  status_label: string;
  status_color: string;
  is_roadmap: boolean;
  published: boolean;
  published_at: string | null;
  votes_count: number;
  created_at: string;
  updated_at: string;
}

interface Props {
  entries: ChangelogEntry[];
  filters: {
    type: string | null;
    category: string | null;
    status: string | null;
    published: string | null;
    search: string | null;
  };
  pagination: {
    page: number;
    per_page: number;
    total_count: number;
    total_pages: number;
  };
  stats: {
    total: number;
    updates: number;
    roadmap: number;
    published: number;
    drafts: number;
  };
  categories: string[];
  statuses: string[];
}

export default function ChangelogIndex({
  entries,
  filters,
  pagination,
  stats,
  categories,
  statuses,
}: Props) {
  const [search, setSearch] = useState(filters.search || "");

  const handleSearch = () => {
    router.get("/admin/changelog_entries", { ...filters, search, page: 1 }, { preserveState: true });
  };

  const handleFilter = (key: string, value: string | null) => {
    router.get("/admin/changelog_entries", { ...filters, [key]: value, page: 1 }, { preserveState: true });
  };

  const handleDelete = async (entry: ChangelogEntry) => {
    if (!confirm(`Delete "${entry.title}"?`)) return;

    try {
      const response = await fetch(`/admin/changelog_entries/${entry.id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "",
          "Accept": "application/json",
        },
      });
      if (response.ok) {
        l2kNotify.success("Deleted", "Entry deleted successfully");
        router.reload();
      } else {
        l2kNotify.error("Error", "Failed to delete entry");
      }
    } catch (error) {
      l2kNotify.error("Error", "Failed to delete entry");
    }
  };

  return (
    <>
      <SEO title="Changelog Admin - Learntokiz" />
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Box>
            <L2KH2 mb="xs">Changelog</L2KH2>
            <L2KText variant="secondary">
              {stats.total} entries ({stats.updates} updates, {stats.roadmap} roadmap)
            </L2KText>
          </Box>
          <Link href="/admin/changelog_entries/new">
            <L2KButton leftSection={<IconPlus size={16} />}>
              New Entry
            </L2KButton>
          </Link>
        </Group>

        {/* Filters */}
        <Group gap="sm">
          <TextInput
            placeholder="Search entries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            leftSection={<IconSearch size={16} />}
            style={{ flex: 1, maxWidth: 300 }}
            styles={{
              input: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderColor: "rgba(255, 255, 255, 0.1)",
                color: "#E0E0E0",
              },
            }}
          />
          <Select
            placeholder="Type"
            value={filters.type || ""}
            onChange={(value) => handleFilter("type", value)}
            data={[
              { value: "", label: "All Types" },
              { value: "updates", label: "Updates" },
              { value: "roadmap", label: "Roadmap" },
            ]}
            clearable
            styles={{
              input: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderColor: "rgba(255, 255, 255, 0.1)",
                color: "#E0E0E0",
              },
            }}
          />
          <Select
            placeholder="Category"
            value={filters.category || ""}
            onChange={(value) => handleFilter("category", value)}
            data={[
              { value: "", label: "All Categories" },
              ...categories.map((c) => ({ value: c, label: c.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) })),
            ]}
            clearable
            styles={{
              input: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderColor: "rgba(255, 255, 255, 0.1)",
                color: "#E0E0E0",
              },
            }}
          />
          <Select
            placeholder="Published"
            value={filters.published || ""}
            onChange={(value) => handleFilter("published", value)}
            data={[
              { value: "", label: "All" },
              { value: "true", label: "Published" },
              { value: "false", label: "Drafts" },
            ]}
            clearable
            styles={{
              input: {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderColor: "rgba(255, 255, 255, 0.1)",
                color: "#E0E0E0",
              },
            }}
          />
        </Group>

        {/* Table */}
        <Box style={{ overflowX: "auto" }}>
          <Table
            horizontalSpacing="md"
            verticalSpacing="sm"
            styles={{
              table: { backgroundColor: "transparent" },
              th: { color: "#888", borderColor: "rgba(255,255,255,0.1)" },
              td: { borderColor: "rgba(255,255,255,0.05)" },
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Votes</Table.Th>
                <Table.Th>Published</Table.Th>
                <Table.Th w={60}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {entries.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <L2KText variant="tertiary" ta="center" py="xl">
                      No entries found
                    </L2KText>
                  </Table.Td>
                </Table.Tr>
              ) : (
                entries.map((entry) => (
                  <Table.Tr key={entry.id}>
                    <Table.Td>
                      <L2KText size="sm" lineClamp={1}>
                        {entry.title}
                      </L2KText>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={entry.is_roadmap ? "purple" : "blue"} variant="light">
                        {entry.is_roadmap ? "Roadmap" : "Update"}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={entry.category_color} variant="light">
                        {entry.category_label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" color={entry.status_color} variant="light">
                        {entry.status_label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <L2KCaption variant="tertiary">{entry.votes_count}</L2KCaption>
                    </Table.Td>
                    <Table.Td>
                      {entry.published ? (
                        <Badge size="sm" color="green" variant="light">
                          {entry.published_at}
                        </Badge>
                      ) : (
                        <Badge size="sm" color="gray" variant="light">
                          Draft
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown
                          style={{
                            backgroundColor: "rgba(23, 23, 23, 0.98)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            component={Link}
                            href={`/admin/changelog_entries/${entry.id}/edit`}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => handleDelete(entry)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <Group justify="center" mt="lg">
            <Pagination
              total={pagination.total_pages}
              value={pagination.page}
              onChange={(page) => router.get("/admin/changelog_entries", { ...filters, page }, { preserveState: true })}
              color="orange"
            />
          </Group>
        )}
      </Stack>
    </>
  );
}

ChangelogIndex.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;
